// context/TokenContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';

const TokenContext = createContext();
export const useTokens = () => useContext(TokenContext);

const ROLES = ['admin', 'vendor', 'customer'];
const DEFAULT_KEYS = ['AuthToken', 'UserInfo', 'CToken', 'UserNotifications'];
const buildKey = (role, key) => `${role}_${key}`;

// Helper to check token expiry
const isTokenExpiredFn = token => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

export const TokenProvider = ({ children }) => {
  const [store, setStore] = useState({});

  // Load initial data from localStorage
  useEffect(() => {
    const loadInitial = () => {
      const initial = {};
      for (const role of ROLES) {
        for (const key of DEFAULT_KEYS) {
          const storageKey = buildKey(role, key);
          const item = localStorage.getItem(storageKey);
          if (item) {
            initial[storageKey] = key === 'AuthToken' ? item : JSON.parse(item);
          }
        }
      }
      setStore(initial);
    };
    loadInitial();
  }, []);

  const setItem = (role, key, value) => {
    const storageKey = buildKey(role, key);
    const valueToStore = typeof value === 'object' ? JSON.stringify(value) : value;
    localStorage.setItem(storageKey, valueToStore);
    setStore(prev => ({ ...prev, [storageKey]: value }));
  };

  const getItem = (role, key) => store[buildKey(role, key)] || null;

  const removeRole = role => {
    for (const key of DEFAULT_KEYS) {
      localStorage.removeItem(buildKey(role, key));
    }
    setStore(prev => {
      const updated = { ...prev };
      for (const key of DEFAULT_KEYS) {
        delete updated[buildKey(role, key)];
      }
      return updated;
    });
  };

  const logout = (role, redirectUrl = '/login') => {
    removeRole(role);
    window.location.href = redirectUrl;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      for (const role of ROLES) {
        const token = getItem(role, 'AuthToken');
        if (isTokenExpiredFn(token)) {
          removeRole(role);
        }
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [store]);

  useEffect(() => {
    const syncStorage = () => {
      const updated = {};
      for (const role of ROLES) {
        for (const key of DEFAULT_KEYS) {
          const storageKey = buildKey(role, key);
          const item = localStorage.getItem(storageKey);
          if (item) {
            updated[storageKey] = key === 'AuthToken' ? item : JSON.parse(item);
          }
        }
      }
      setStore(updated);
    };
    window.addEventListener('storage', syncStorage);
    return () => window.removeEventListener('storage', syncStorage);
  }, []);

  return (
    <TokenContext.Provider
      value={{
        setItem,
        getItem,
        removeRole,
        logout,
        isTokenExpired: isTokenExpiredFn,
        store,
      }}
    >
      {children}
    </TokenContext.Provider>
  );
};
